import { useState, type FormEvent } from "react";
import { ConferenceBrandImage } from "./ConferenceBrandImage";
import {
  type ConferenceDashboardClient,
  type ConferenceDiscoveryPreview,
} from "./portfolio-overview-api";
import { portfolioErrorMessageKey } from "./portfolio-api-errors";
import { FIREBASE_PROJECT_ID_PATTERN } from "./project-id";
import {
  bidiIsolationAttributes,
  useLocale,
  type MessageKey,
} from "./localization";

type DiscoveryState =
  | { readonly kind: "idle" }
  | { readonly kind: "discovering" }
  | { readonly kind: "preview"; readonly preview: ConferenceDiscoveryPreview }
  | { readonly kind: "error"; readonly message: MessageKey };

type ActivationState = "idle" | "activating" | "activated" | "error";

function Capability({
  label,
  capability,
}: {
  readonly label: string;
  readonly capability: ConferenceDiscoveryPreview["capabilities"]["ga4Metrics"];
}) {
  const { t } = useLocale();
  return (
    <li>
      <span>{label}</span>
      <strong>
        {capability.supported
          ? t("connect.capability.automated")
          : t("connect.capability.unavailable")}
      </strong>
    </li>
  );
}

function ConferencePreview({
  preview,
  activationState,
  activationError,
  onActivate,
}: {
  readonly preview: ConferenceDiscoveryPreview;
  readonly activationState: ActivationState;
  readonly activationError: MessageKey | null;
  readonly onActivate: () => void;
}) {
  const { date, t } = useLocale();
  const statusKey = {
    planned: "connect.status.planned",
    active: "connect.status.active",
    ended: "connect.status.ended",
    archived: "connect.status.archived",
  } as const;
  const technicalValueProps = bidiIsolationAttributes("ltr");
  return (
    <section
      className="connector-preview"
      aria-labelledby="connector-preview-title"
    >
      <header className="connector-preview__header">
        <ConferenceBrandImage
          conferenceId={preview.projectId}
          conferenceName={preview.name}
          configuredLogo={preview.logoUrl}
          width="88"
          height="88"
        />
        <div>
          <p>{t("connect.manifestDiscovered")}</p>
          <h2 id="connector-preview-title">
            <bdi dir="auto">{preview.name}</bdi>
          </h2>
          <span className="status-label">
            <span aria-hidden="true" /> {t(statusKey[preview.status])}
          </span>
        </div>
      </header>

      <p className="connector-preview__notice">{t("connect.metadataLocked")}</p>

      <dl className="connector-preview__metadata">
        <div>
          <dt>{t("connect.field.projectId")}</dt>
          <dd {...technicalValueProps}>{preview.projectId}</dd>
        </div>
        <div>
          <dt>{t("connect.field.conferenceKey")}</dt>
          <dd {...technicalValueProps}>{preview.conferenceKey}</dd>
        </div>
        <div>
          <dt>{t("connect.field.starts")}</dt>
          <dd>
            {date(preview.startsAt, {
              timeZone: "Asia/Riyadh",
              dateStyle: "long",
              timeStyle: "short",
            })}
          </dd>
        </div>
        <div>
          <dt>{t("connect.field.ends")}</dt>
          <dd>
            {date(preview.endsAt, {
              timeZone: "Asia/Riyadh",
              dateStyle: "long",
              timeStyle: "short",
            })}
          </dd>
        </div>
        <div>
          <dt>{t("connect.field.timezone")}</dt>
          <dd {...technicalValueProps}>{preview.timezone}</dd>
        </div>
        <div>
          <dt>{t("connect.field.connectorRegion")}</dt>
          <dd {...technicalValueProps}>{preview.connectorRegion}</dd>
        </div>
        <div>
          <dt>{t("connect.field.connectorVersion")}</dt>
          <dd {...technicalValueProps}>
            {preview.componentVersions.connector}
          </dd>
        </div>
        <div>
          <dt>{t("connect.field.adapterVersion")}</dt>
          <dd {...technicalValueProps}>{preview.componentVersions.adapter}</dd>
        </div>
        <div>
          <dt>{t("connect.field.sourceVersion")}</dt>
          <dd {...technicalValueProps}>{preview.componentVersions.source}</dd>
        </div>
      </dl>

      <section className="connector-preview__objective">
        <h3>{t("connect.objective.title")}</h3>
        <p>
          {preview.objective ? (
            <bdi dir="auto">{preview.objective}</bdi>
          ) : (
            t("connect.objective.empty")
          )}
        </p>
      </section>

      <section className="connector-preview__capabilities">
        <h3>{t("connect.coverage.title")}</h3>
        <ul>
          <Capability
            label={t("connect.coverage.registrations")}
            capability={preview.capabilities.registrationMetrics}
          />
          <Capability
            label={t("connect.coverage.payments")}
            capability={preview.capabilities.paymentMetrics}
          />
          <Capability
            label={t("connect.coverage.analytics")}
            capability={preview.capabilities.ga4Metrics}
          />
        </ul>
      </section>

      <section
        className="connector-preview__activation"
        aria-labelledby="connector-activation-title"
      >
        <div>
          <h3 id="connector-activation-title">
            {t("connect.activation.title")}
          </h3>
          <p>{t("connect.activation.body")}</p>
        </div>
        {activationState === "activated" ? (
          <strong role="status">{t("connect.activation.complete")}</strong>
        ) : (
          <button
            className="button button--primary"
            type="button"
            disabled={activationState === "activating"}
            onClick={onActivate}
          >
            {activationState === "activating"
              ? t("connect.activation.activating")
              : t("connect.activation.action")}
          </button>
        )}
        {activationState === "error" ? (
          <p className="form-message form-message--error" role="alert">
            {t(activationError ?? "connect.activation.error")}
          </p>
        ) : null}
      </section>
    </section>
  );
}

export function ConnectConferencePanel({
  client,
}: {
  readonly client: ConferenceDashboardClient;
}) {
  const { t } = useLocale();
  const [projectId, setProjectId] = useState("");
  const [state, setState] = useState<DiscoveryState>({ kind: "idle" });
  const [activationState, setActivationState] =
    useState<ActivationState>("idle");
  const [activationError, setActivationError] = useState<MessageKey | null>(
    null,
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState({ kind: "discovering" });
    try {
      const preview = await client.discoverConference(projectId.trim());
      setState({ kind: "preview", preview });
      setActivationState("idle");
    } catch (error) {
      setState({
        kind: "error",
        message: portfolioErrorMessageKey(error, "connect.discovery.notFound"),
      });
    }
  }

  async function activate(preview: ConferenceDiscoveryPreview) {
    setActivationState("activating");
    setActivationError(null);
    try {
      await client.activateConferenceRegistration(preview.projectId);
      setActivationState("activated");
    } catch (error) {
      setActivationState("error");
      setActivationError(
        portfolioErrorMessageKey(error, "connect.activation.error"),
      );
    }
  }

  return (
    <div
      className={`connect-conference-layout${state.kind === "preview" ? "" : " connect-conference-layout--initial"}`}
    >
      <section
        className="connect-conference-card"
        aria-labelledby="connect-project-title"
      >
        <header>
          <p>{t("connect.discovery.kicker")}</p>
          <h2 id="connect-project-title">{t("connect.discovery.title")}</h2>
          <span>{t("connect.discovery.body")}</span>
        </header>
        <form onSubmit={(event) => void submit(event)}>
          <label htmlFor="connector-project-id">
            {t("connect.field.projectId")}
          </label>
          <input
            id="connector-project-id"
            name="projectId"
            type="text"
            autoComplete="off"
            spellCheck={false}
            aria-describedby="connector-project-hint"
            aria-invalid={state.kind === "error" ? true : undefined}
            minLength={5}
            maxLength={30}
            pattern={FIREBASE_PROJECT_ID_PATTERN}
            placeholder={t("connect.discovery.projectPlaceholder")}
            {...bidiIsolationAttributes("ltr")}
            required
            disabled={state.kind === "discovering"}
            value={projectId}
            onChange={(event) => {
              setProjectId(event.currentTarget.value.toLowerCase());
              if (state.kind !== "idle") setState({ kind: "idle" });
              setActivationState("idle");
            }}
          />
          <p id="connector-project-hint">{t("connect.discovery.manualOnly")}</p>
          <button
            className="button button--primary"
            type="submit"
            disabled={state.kind === "discovering"}
          >
            {state.kind === "discovering"
              ? t("connect.discovery.discovering")
              : t("connect.discovery.action")}
          </button>
        </form>
        {state.kind === "error" ? (
          <p className="form-message form-message--error" role="alert">
            {t(state.message)}
          </p>
        ) : null}
      </section>

      {state.kind === "preview" ? (
        <ConferencePreview
          preview={state.preview}
          activationState={activationState}
          activationError={activationError}
          onActivate={() => void activate(state.preview)}
        />
      ) : (
        <section className="connector-preview connector-preview--empty">
          <span aria-hidden="true">↗</span>
          <h2>{t("connect.preview.title")}</h2>
          <p>{t("connect.preview.empty")}</p>
        </section>
      )}
    </div>
  );
}
